import React, { useState } from "react";
import { createPortal } from "react-dom";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import CashKeypad from "./CashKeypad";
import { formatAmount } from "../utils/money";
import { pressAmountKey } from "../utils/amountEntry";

/**
 * The drawer is closed, so nothing can be sold until someone counts what is
 * in it and opens it.
 *
 * The count is blind: the till never says what the last close left, so what
 * is typed here is what is really there. A difference goes to the owner's
 * journal, not to the person counting.
 *
 * Rendered into document.body, like the till lock.
 */
const DrawerGate = ({ currency, onOpened, onLock }) => {
  const { t } = useI18n();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const open = async () => {
    setBusy(true);
    setError("");
    try {
      await posAPI.openDrawer(parseFloat(amount || "0"));
      onOpened();
    } catch (err) {
      // Someone on another till may have opened it meanwhile
      if (err.response?.data?.code === "DRAWER_ALREADY_OPEN") {
        onOpened();
        return;
      }
      setError(err.response?.data?.message || t("pos.drawer.failed"));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("pos.drawer.openTitle")}
      className="fixed inset-0 z-[65] bg-slate-950 text-slate-100 overflow-y-auto overscroll-contain"
    >
      <div className="min-h-full flex flex-col items-center justify-center px-6 py-12 pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
        <span className="text-4xl leading-none" aria-hidden="true">
          💵
        </span>
        <p className="mt-3 text-lg font-bold text-white text-center">{t("pos.drawer.openTitle")}</p>
        <p className="mt-1 max-w-xs text-sm text-slate-400 text-center">{t("pos.drawer.openHint")}</p>

        <p className="mt-6 text-4xl font-extrabold tabular-nums text-white" dir="ltr">
          {amount || formatAmount(0)} <span className="text-base text-slate-400">{currency}</span>
        </p>

        <div className="mt-4 w-full max-w-xs">
          <CashKeypad onPress={(key) => setAmount((current) => pressAmountKey(current, key))} />
        </div>

        {error && <p className="mt-3 text-sm font-semibold text-red-400">{error}</p>}

        <button
          type="button"
          disabled={busy}
          onClick={open}
          className="mt-5 w-full max-w-xs h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-white text-lg font-bold disabled:opacity-50"
        >
          {busy ? t("common.saving") : t("pos.drawer.open")}
        </button>

        <button
          type="button"
          onClick={onLock}
          className="mt-6 text-sm font-semibold text-slate-400 hover:text-slate-200"
        >
          🔒 {t("pos.header.lock")}
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default DrawerGate;
